"use client";
import React, { useContext, useEffect, useRef, useState } from "react";
import Icons from "../../components/Icons";
import { Context } from "../contexts/AppContext";
import { v4 as uuidv4 } from "uuid";
import { getData, postData } from "../../lib/utils";

function UploadVideo(props) {
  const [videoFiles, setVideoFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const xhrRefs = useRef({});
  const [totalFileUploaded, setTotalFileUploaded] = useState(0);

  const { value, progress } = useContext(Context);
  const [appState] = value;
    const {
    currentFolderData,
    currentGalleryData,
    currentFolderName,
    currentGalleryName,
  } = appState;

  const { showProgressBar, cancelledIdsRef } = progress;

const sanitizeFileName = (name) => name.replace(/[^a-zA-Z0-9_.]/g, "");



const handleFileChange = (event) => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;

  // ✅ Get available credits (assumed stored in MB)
  let availableCreditsMB = appState?.adminDetails?.credits || 0;

  // Convert credits display unit (MB or GB)
  const creditsDisplay =
    availableCreditsMB >= 1024
      ? `${(availableCreditsMB / 1024).toFixed(2)} GB`
      : `${availableCreditsMB.toFixed(2)} MB`;

  // ✅ Calculate total size of all selected files in MB
  const totalSizeMB = files.reduce((sum, file) => sum + file.size / (1024 * 1024), 0);

  // Convert upload size display (MB or GB)
  const uploadDisplay =
    totalSizeMB >= 1024
      ? `${(totalSizeMB / 1024).toFixed(2)} GB`
      : `${totalSizeMB.toFixed(2)} MB`;

  // ✅ Compare (always in MB for logic)
  if (totalSizeMB > availableCreditsMB) {
    alert(
      `⚠️ Not enough credits to upload!\n` +
      `🧾 Total video size: ${uploadDisplay}\n` +
      `💰 Available credits: ${creditsDisplay}\n`+
      `Purchase New credits to upload` 
    );
    return; // ❌ Stop upload
  }

  // ✅ Otherwise, continue with upload
  setVideoFiles(files);
};



  // Warn user if upload in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading || videoFiles.length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading, videoFiles.length]);

  useEffect(() => {
    if (videoFiles.length > 0) handleUpload();
  }, [videoFiles]);

  // Backend API calls
  const startMultipartUpload = async (fileName, fileType) =>
  {
     const s3Key = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${fileName}`;
  return  await postData(`products/start-multipart-upload?fileName=${encodeURIComponent(s3Key)}&fileType=${fileType}`);
  }

  const getPartUrl = async (fileName, uploadId, partNumber) => {
      const s3Key = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${fileName}`;
   return await getData(
      `products/get-part-url?fileName=${encodeURIComponent(s3Key)}&uploadId=${uploadId}&partNumber=${partNumber}`
    );
  }

  const completeMultipartUpload = async (_fileName, uploadId, parts) =>
  {
      const fileName = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${_fileName}`;
   return await postData(`products/complete-multipart-upload`, {
      fileName, uploadId, parts 
    });
  }

  // Multipart upload logic
  const uploadMultipartFile = async (file, uniqueId, index) => {
    const cleanName = sanitizeFileName(file.name);

    // Dynamic chunk size: 50MB–200MB
    const chunkSize = Math.min(Math.max(file.size / 1000, 50 * 1024 * 1024), 200 * 1024 * 1024);
    const totalParts = Math.ceil(file.size / chunkSize);

    showProgressBar?.(cleanName, 0, uniqueId, "uploading");

    try {
      const { uploadId } = await startMultipartUpload(cleanName, file.type);
      let parts = [];
      let uploadedBytes = 0;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const { url } = await getPartUrl(cleanName, uploadId, partNumber);

        const xhr = new XMLHttpRequest();
        xhrRefs.current[uniqueId] = xhr;

        await new Promise((resolve, reject) => {
          xhr.open("PUT", url, true);

          if (cancelledIdsRef?.has?.(uniqueId)) {
            xhr.abort();
            showProgressBar?.(cleanName, 0, uniqueId, "cancelled");
            return reject("Upload cancelled");
          }

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && !cancelledIdsRef?.has?.(uniqueId)) {
              uploadedBytes = start + event.loaded;
              const percent = Math.round((uploadedBytes / file.size) * 100);
              showProgressBar?.(cleanName, percent, uniqueId, "uploading");
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              const eTag = xhr.getResponseHeader("ETag");
              parts.push({ ETag: eTag, PartNumber: partNumber });
              resolve();
            } else reject(`Part ${partNumber} failed`);
          };

          xhr.onerror = () => reject(`Network error on part ${partNumber}`);
          xhr.send(chunk);
        });
      }

      // Complete upload
      await completeMultipartUpload(cleanName, uploadId, parts);

      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setTotalFileUploaded((prev) => prev + 1);
      props?.videoUploadedDone?.({ ...file, name: cleanName }, fileSizeMB, index, videoFiles.length, uniqueId);
      showProgressBar?.(cleanName, 100, uniqueId, "completed");
    } catch (err) {
      console.error("Multipart upload error:", err);
      showProgressBar?.(cleanName, 0, uniqueId, cancelledIdsRef?.has?.(uniqueId) ? "cancelled" : "failed");
    } finally {
      delete xhrRefs.current[uniqueId];
    }
  };

  // Handle upload queue with concurrency
  const handleUpload = async () => {
    setIsUploading(true);

    const MAX_CONCURRENT_UPLOADS = 3;
    let currentIndex = 0;
    let activeUploads = 0;

    videoFiles.forEach((file) => {
      const uniqueId = uuidv4();
      file._uploadId = uniqueId;
      showProgressBar?.(sanitizeFileName(file.name), 0, uniqueId, "Queued");
    });

    const startNextUpload = () => {
      if (currentIndex >= videoFiles.length && activeUploads === 0) {
        setIsUploading(false);
        setVideoFiles([]);
        return;
      }
      if (currentIndex >= videoFiles.length) return;

      const file = videoFiles[currentIndex];
      const uniqueId = file._uploadId;
      const index = currentIndex;
      currentIndex++;
      activeUploads++;

      (async () => {
        try {
          await uploadMultipartFile(file, uniqueId, index);
        } catch (err) {
          console.error("Upload error:", err);
        } finally {
          activeUploads--;
          startNextUpload();
        }
      })();
    };

    for (let i = 0; i < MAX_CONCURRENT_UPLOADS; i++) startNextUpload();
  };

  return (
    <div>
      <input type="file" accept="video/*" multiple onChange={handleFileChange} id="fileInput" style={{ display: "none" }} />
      <button
        onClick={() => document.getElementById("fileInput").click()}
        className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded flex gap-2 items-center whitespace-nowrap"
      >
        <Icons.Uploads /> Upload Videos
      </button>
    </div>
  );
}

export default UploadVideo;
